import { GUNBOUND_MODE, mergeModeConfig, type ModeConfig } from './gameModes';

export interface SuddenDeathSettings {
  enabled: boolean;
  turnsBeforeStart: number; // Total turns before sudden death triggers
  waterRisePerTurn: number; // Pixels water rises each turn during sudden death
}

export interface GameSettings {
  hazards: HazardSettings;
  turn: TurnSettings;
  worm: WormSettings;
  terrain: TerrainSettings;
  suddenDeath: SuddenDeathSettings;
  /** Declarative mode ruleset (classic preset reproduces legacy behavior). */
  mode: ModeConfig;
}

export interface HazardSettings {
  oilBarrels: boolean;
  oilBarrelCount: { min: number; max: number };
  prePlacedMines: boolean;
  prePlacedMineCount: { min: number; max: number };
}

export type TerrainStyle =
  | 'open'
  | 'cavern'
  | 'random'
  | 'island'
  | 'bridges'
  | 'towers'
  | 'fortress'
  | 'tunnels'
  | 'platforms';
export type TerrainTheme = 'dirt' | 'snow' | 'hell' | 'jungle' | 'desert' | 'alien' | 'random';
export type MapComplexity = 'simple' | 'normal' | 'complex';

export interface TerrainSettings {
  girders: boolean;
  style: TerrainStyle;
  theme: TerrainTheme;
  complexity: MapComplexity;
  floatingIslands: boolean;
  bridges: boolean;
  pillars: boolean;
  caves: boolean;
}

export interface TurnSettings {
  turnTime: number;
  retreatTime: number;
  betweenTurnsTime: number;
}

export interface WormSettings {
  initialHealth: number;
  crateDropChance: number;
  gravesOnDeath: boolean;
  infiniteAmmo: boolean;
  /** Worms per team (1-4). */
  wormsPerTeam: number;
}

export const DEFAULT_GAME_SETTINGS: GameSettings = {
  hazards: {
    oilBarrels: true,
    oilBarrelCount: { min: 3, max: 6 },
    prePlacedMines: true,
    prePlacedMineCount: { min: 2, max: 4 },
  },
  turn: {
    turnTime: 30,
    retreatTime: 5,
    betweenTurnsTime: 3,
  },
  worm: {
    initialHealth: 200,
    crateDropChance: 0.15,
    gravesOnDeath: true,
    infiniteAmmo: false,
    wormsPerTeam: 1,
  },
  terrain: {
    girders: true,
    style: 'random' as TerrainStyle,
    theme: 'random' as TerrainTheme,
    complexity: 'normal' as MapComplexity,
    floatingIslands: true,
    bridges: true,
    pillars: false,
    caves: false,
  },
  suddenDeath: {
    enabled: true,
    turnsBeforeStart: 30,
    waterRisePerTurn: 25,
  },
  mode: GUNBOUND_MODE,
};

export function createGameSettings(overrides: Partial<GameSettings> = {}): GameSettings {
  // Always build a fresh object (nested sections included) so callers can
  // mutate the result without corrupting DEFAULT_GAME_SETTINGS or presets.
  return {
    hazards: {
      ...DEFAULT_GAME_SETTINGS.hazards,
      ...overrides.hazards,
    },
    turn: {
      ...DEFAULT_GAME_SETTINGS.turn,
      ...overrides.turn,
    },
    worm: {
      ...DEFAULT_GAME_SETTINGS.worm,
      ...overrides.worm,
    },
    terrain: {
      ...DEFAULT_GAME_SETTINGS.terrain,
      ...overrides.terrain,
    },
    suddenDeath: {
      ...DEFAULT_GAME_SETTINGS.suddenDeath,
      ...overrides.suddenDeath,
    },
    mode: mergeModeConfig(DEFAULT_GAME_SETTINGS.mode, overrides.mode),
  };
}

export function serializeSettings(settings: GameSettings): string {
  return JSON.stringify(settings);
}

export function deserializeSettings(json: string): GameSettings {
  try {
    const parsed = JSON.parse(json);
    return createGameSettings(parsed);
  } catch {
    return DEFAULT_GAME_SETTINGS;
  }
}
